import {AfterViewInit, Component, computed, effect, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {CoreService} from '../../services/core.service';
import {Observable} from 'rxjs';
import {QueryPatient} from '@xmedical/core';
import {map, shareReplay} from 'rxjs/operators';
import {Router} from '@angular/router';
import {PatientService} from 'src/app/modules/patients/services/patient.service';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {UntypedFormControl} from '@angular/forms';
import {ScrollStrategy, ScrollStrategyOptions} from '@angular/cdk/overlay';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'app-patient-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: false
})
export class PatientSearchComponent implements AfterViewInit {

  private scrollStrategyOptions = inject(ScrollStrategyOptions);
  private patientService = inject(PatientService);
  private coreService = inject(CoreService);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);

  formControl = new UntypedFormControl();
  error = '';
  searching = false;
  dataSource = new MatTableDataSource<QueryPatient>(null);
  searchResults: QueryPatient[] = [];
  isVisible = false;
  showAll = false;
  searchObservable = this.patientService.searchPatients;
  @ViewChild(MatSort) sort: MatSort;
  scrollStrategy: ScrollStrategy = this.scrollStrategyOptions.block();

  // Computed signal for practice settings
  readonly settings = this.coreService.practiceSettings;

  // Computed signal for displayed columns based on settings
  readonly displayedColumns = computed(() => {
    const idColumn = this.settings()?.useFileNumberAsPatientProfileId ? 'fileNumber' : 'profileId';
    return ['status', idColumn, 'surname', 'firstName', 'nickname', 'cell', 'tel', 'identityNumber'];
  });


  isTablet$: Observable<any> = this.breakpointObserver.observe(Breakpoints.Tablet)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );


  isNotTablet$: Observable<any> = this.breakpointObserver.observe(Breakpoints.Tablet)
    .pipe(
      map(result => !result.matches),
      shareReplay()
    );

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  isNotHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => !result.matches),
      shareReplay()
    );


  get hasMoreResults(): boolean {
    return this.searchResults?.length > this.dataSource?.data?.length;
  }

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasResults(): boolean {
    return this.resultsReady && this.dataSource.data.length > 0;
  }

  get resultsReady(): boolean {
    return !this.searching && this.dataSource.data !== null && this.dataSource.data.length >= 0 && this.formControl?.value?.length > 0;
  }

  get filterByHighestQueryWeight(): QueryPatient[] {
    const bestRanking = [...new Set(this.searchResults.map((patient) => patient.queryWeight))][0];
    const result: QueryPatient[] = [];
    return this.searchResults.filter((patient) => patient.queryWeight === bestRanking);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  rowSelected(option: QueryPatient) {
    this.closeSearch();
    this.patientService.openTabWithQueryResult(option);
  }

  onSearchResults(results: QueryPatient[]) {
    if (results === null) {
      results = [];
    }
    this.searchResults = results;
    this.updateDataSource();
    this.error = '';
  }

  onSearchStatusChange(isSearching: boolean) {
    this.searching = isSearching;
  }

  onError(errorMessage: string) {
    this.error = errorMessage;
  }

  openSearch() {
    this.isVisible = true;
  }

  closeSearch() {
    this.isVisible = false;
    this.cancelSearch();
  }

  cancelSearch() {
    this.formControl.setValue('');
    this.dataSource = new MatTableDataSource<QueryPatient>(null);
    this.searchResults = [];
    this.showAll = false;
    this.error = '';
  }

  toggleAllResults() {
    this.showAll = !this.showAll;
    this.updateDataSource();
  }

  addNewPatient() {
    this.closeSearch();
    this.router.navigateByUrl('/patients/new');
  }

  overlayClosed(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeSearch();
    }
  }

  private updateDataSource() {
    const data = this.showAll ? this.searchResults : this.filterByHighestQueryWeight;
    this.dataSource.data = [...data];
    setTimeout(() => {
      this.dataSource.sort = this.sort;
    });
  }
}
